import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import metricsApi from '../services/metricsApi';
import '../css/Home.css';

const TickerDashboard = () => {
    const location = useLocation();
    const { symbol } = useParams();
    const [tickerData, setTickerData] = useState(null);
    const [metricsData, setMetricsData] = useState(null);
    const [predictionsData, setPredictionsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Parse URL parameters
    const searchParams = new URLSearchParams(location.search);
    const period = searchParams.get('period') || '3mo';
    const params = {
        symbol: searchParams.get('ticker'),
        period: searchParams.get('period') || '1y',
        prediction_days: parseInt(searchParams.get('prediction_days')) || 30,
        include_news: searchParams.get('include_news') === 'true',
        risk_tolerance: parseInt(searchParams.get('risk_tolerance')) || 50
    };    
    
    console.info("Given User Param: ", symbol)
    useEffect(() => {
        const fetchTickerData = async () => {
            try {
                setLoading(true);
                // Parse URL parameters
                const queryString = new URLSearchParams({
                    symbol: params.ticker,
                    period: params.period,
                    prediction_days: params.prediction_days,
                    include_news: params.include_news,
                    risk_tolerance: params.risk_tolerance
                }).toString();              
                // Simulate API calls - replace with actual API endpoints
                const generalResponse = await metricsApi.getGeneralDetails(symbol);
                const metricsResponse = await metricsApi.getMetrics(symbol, period);
                // const predictionsResponse = await metricsApi.getPredictions(symbol, "1y", 30);
                // const predictionsResponse = await metricsApi.getPredictions(symbol, period, prediction_days, include_news, risk_tolerance);                

                setTickerData(generalResponse);
                setMetricsData(metricsResponse);
                // setPredictionsData(predictionsResponse);
            } catch (err) {
                setError('Failed to fetch ticker data');
                console.error('Error fetching ticker data:', err);
            } finally {
                setLoading(false);
            }
        };

        if (symbol) {
            fetchTickerData();
        }
    }, [location.search]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading {symbol} data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg max-w-md">
                        <p className="font-semibold">Error</p>
                        <p>{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-center space-x-4 mb-4 lg:mb-0">
                            <div className="bg-blue-100 p-3 rounded-lg">
                                <span className="text-2xl font-bold text-blue-600">{tickerData.ticker}</span>
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">{tickerData.name}</h1>
                                <p className="text-gray-600">As of {new Date(tickerData.as_of_date).toLocaleDateString()}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-3xl font-bold text-gray-900">
                                ${tickerData.current_price}
                                <span className="text-lg text-gray-600 ml-2">{tickerData.currency}</span>
                            </div>
                            <div className="text-sm text-gray-500">
                                Market Cap: ${(tickerData.marketCap / 1e9).toFixed(2)}B
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - General Details */}
                    <div className="lg:col-span-1 space-y-6">
                        <GeneralDetailsCard data={tickerData} />
                        <ValuationMetricsCard data={tickerData} />
                    </div>

                    {/* Middle Column - Technical Analysis
                    <div className="lg:col-span-1 space-y-6">
                        <PredictionsCard data={predictionsData} />
                        <TradingRecommendationCard data={predictionsData} />                        
                        <RiskMetricsCard data={predictionsData} />
                    </div>                     */}

                    {/* Right Column - Predictions & Recommendations */}
                    <div className="lg:col-span-1 space-y-6">
                        <TechnicalIndicatorsCard data={metricsData} />
                        <TradingSignalsCard data={metricsData} />
                    </div>

                </div>
            </div>
        </div>
    );
};

// Component for General Details
const GeneralDetailsCard = ({ data }) => (
    <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
            General Information
        </h2>
        <div className="space-y-3">
            <InfoRow label="Ticker" value={data.ticker} />
            <InfoRow label="Company" value={data.name} />
            <InfoRow label="Current Price" value={`$${data.current_price}`} />
            <InfoRow label="Currency" value={data.currency} />
            <InfoRow label="Market Cap" value={`$${(data.marketCap / 1e9).toFixed(2)}B`} />
            <InfoRow label="Volume" value={data.volume?.toLocaleString()} />
            <InfoRow label="As of Date" value={new Date(data.as_of_date).toLocaleDateString()} />
        </div>
    </div>
);

// Component for Valuation Metrics
const ValuationMetricsCard = ({ data }) => (
    <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            Valuation Metrics
        </h2>
        <div className="space-y-3">
            <MetricRow 
                label="P/E Ratio" 
                // value={data.price_to_earnings?.toFixed(2)} 
                value={data.price_to_earnings} 
                goodRange={[0, 15]}
                currentValue={data.price_to_earnings}
            />
            <MetricRow 
                label="P/B Ratio" 
                value={data.price_to_book} 
                goodRange={[0, 3]}
                currentValue={data.price_to_book}
            />
            <MetricRow 
                label="Debt to Equity" 
                value={`${data.debt_to_equity}%`} 
                goodRange={[0, 50]}
                currentValue={data.debt_to_equity}
                reverse={true}
            />
            <MetricRow 
                label="Return on Equity" 
                value={`${(data.return_on_equity * 100)}%`} 
                goodRange={[15, 100]}
                currentValue={data.return_on_equity * 100}
            />
            <InfoRow label="Free Cash Flow" value={`$${(data.free_cash_flow / 1e6).toFixed(0)}M`} />
            <InfoRow label="PEG Ratio" value={data.peg_ratio} />
        </div>
    </div>
);

// Component for Technical Indicators
const TechnicalIndicatorsCard = ({ data }) => {
    const indicators = data?.technical_indicators;
    if (!indicators) return null;

    return (
        <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                Technical Indicators
            </h2>
            <div className="grid grid-cols-2 gap-4">
                <IndicatorBox label="Open" value={indicators.Open?.toFixed(2)} />
                <IndicatorBox label="High" value={indicators.High?.toFixed(2)} />
                <IndicatorBox label="Low" value={indicators.Low?.toFixed(2)} />
                <IndicatorBox label="Close" value={indicators.Close?.toFixed(2)} />
                <IndicatorBox label="Volume" value={(indicators.Volume / 1e6).toFixed(1) + 'M'} />
                <IndicatorBox label="RSI" value={indicators.RSI?.toFixed(1)} 
                    status={indicators.RSI > 70 ? 'overbought' : indicators.RSI < 30 ? 'oversold' : 'neutral'} />
                <IndicatorBox label="MACD" value={indicators.MACD?.toFixed(3)} />
                <IndicatorBox label="ATR" value={indicators.ATR?.toFixed(3)} />
            </div>
        </div>
    );
};

// Component for Trading Signals
const TradingSignalsCard = ({ data }) => {
    const signals = data?.signals;
    if (!signals) return null;

    const getSignalColor = (signal) => {
        switch (signal) {
            case 'bullish': return 'text-green-600 bg-green-100';
            case 'bearish': return 'text-red-600 bg-red-100';
            case 'overbought': return 'text-orange-600 bg-orange-100';
            case 'oversold': return 'text-blue-600 bg-blue-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                Trading Signals
            </h2>
            <div className="space-y-3">
                <div className="flex justify-between items-center">
                    <span className="text-gray-600">RSI Signal:</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSignalColor(signals.rsi_signal)}`}>
                        {signals.rsi_signal}
                    </span>
                </div>
                <div className="flex justify-between items-center">
                    <span className="text-gray-600">MACD Signal:</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSignalColor(signals.macd_signal)}`}>
                        {signals.macd_signal}
                    </span>
                </div>
                <div className="flex justify-between items-center">
                    <span className="text-gray-600">Trend:</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSignalColor(signals.trend)}`}>
                        {signals.trend}
                    </span>
                </div>
                <div className="flex justify-between items-center">
                    <span className="text-gray-600">Signal Strength:</span>
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${signals.signal_strength * 100}%` }}
                        ></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Component for Predictions
const PredictionsCard = ({ data }) => {
    const prediction = data?.prediction;
    if (!prediction) return null;

    return (
        <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                AI Predictions
            </h2>
            <div className="space-y-4">
                <div className="text-center p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">
                        {prediction.price_direction.toUpperCase()}
                    </div>
                    <div className="text-sm text-gray-600">Predicted Direction</div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                    <PredictionBox 
                        label="Confidence" 
                        value={`${(prediction.confidence * 100).toFixed(1)}%`}
                        level={prediction.confidence_level}
                    />
                    <PredictionBox 
                        label="Predicted Return" 
                        value={`${prediction.predicted_return_pct.toFixed(2)}%`}
                        positive={prediction.predicted_return_pct > 0}
                    />
                    <PredictionBox label="Time Horizon" value={`${prediction.time_horizon_days} days`} />
                    <PredictionBox label="Technical Score" value={prediction.technical_score?.toFixed(3)} />
                </div>
            </div>
        </div>
    );
};

// Component for Risk Metrics
const RiskMetricsCard = ({ data }) => {
    const risk = data?.risk_metrics;
    if (!risk) return null;

    return (
        <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                Risk Metrics
            </h2>
            <div className="space-y-3">
                <RiskMetricRow label="Volatility" value={(risk.volatility * 100).toFixed(2) + '%'} />
                <RiskMetricRow label="Sharpe Ratio" value={risk.sharpe_ratio?.toFixed(3)} />
                <RiskMetricRow label="Max Drawdown" value={(risk.max_drawdown * 100).toFixed(2) + '%'} />
                <RiskMetricRow label="VaR (95%)" value={(risk.var_95 * 100).toFixed(2) + '%'} />
                <RiskMetricRow label="Current RSI" value={risk.current_rsi?.toFixed(1)} />
            </div>
        </div>
    );
};

// Component for Trading Recommendations
const TradingRecommendationCard = ({ data }) => {
    const recommendation = data?.trading_recommendation;
    if (!recommendation) return null;

    return (
        <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                Trading Recommendation
            </h2>
            <div className="text-center mb-4">
                <div className={`text-2xl font-bold ${
                    recommendation.action === 'BUY' ? 'text-green-600' : 'text-red-600'
                }`}>
                    {recommendation.action}
                </div>
                <div className="text-sm text-gray-600">Confidence: {recommendation.confidence_level}</div>
            </div>
            <div className="space-y-3">
                <InfoRow label="Stop Loss" value={`$${recommendation.stop_loss?.toFixed(2)}`} />
                <InfoRow label="Take Profit" value={`$${recommendation.take_profit?.toFixed(2)}`} />
                <InfoRow label="Position Size" value={recommendation.position_size?.toLocaleString()} />
            </div>
        </div>
    );
};

// Reusable Components
const InfoRow = ({ label, value }) => (
    <div className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
        <span className="text-gray-600 text-sm">{label}:</span>
        <span className="text-gray-900 font-medium">{value}</span>
    </div>
);

const MetricRow = ({ label, value, goodRange, currentValue, reverse = false }) => {
    const isGood = reverse 
        ? (currentValue <= goodRange[1])
        : (currentValue >= goodRange[0] && currentValue <= goodRange[1]);
    
    return (
        <div className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
            <span className="text-gray-600 text-sm">{label}:</span>
            <span className={`font-medium ${isGood ? 'text-green-600' : 'text-red-600'}`}>
                {value}
            </span>
        </div>
    );
};

const IndicatorBox = ({ label, value, status }) => (
    <div className="text-center p-3 bg-gray-50 rounded-lg">
        <div className="text-xs text-gray-500 mb-1">{label}</div>
        <div className={`text-sm font-semibold ${
            status === 'overbought' ? 'text-orange-600' :
            status === 'oversold' ? 'text-blue-600' : 'text-gray-900'
        }`}>
            {value}
        </div>
    </div>
);

const PredictionBox = ({ label, value, level, positive }) => (
    <div className="text-center p-2 bg-gray-50 rounded-lg">
        <div className="text-xs text-gray-500 mb-1">{label}</div>
        <div className={`text-sm font-semibold ${
            level === 'high' ? 'text-green-600' :
            level === 'medium' ? 'text-yellow-600' :
            level === 'low' ? 'text-red-600' :
            positive ? 'text-green-600' : 'text-red-600'
        }`}>
            {value}
        </div>
    </div>
);

const RiskMetricRow = ({ label, value }) => (
    <div className="flex justify-between items-center py-2">
        <span className="text-gray-600 text-sm">{label}:</span>
        <span className="text-gray-900 font-medium text-sm">{value}</span>
    </div>
);

export default TickerDashboard;